import React, { useEffect, useState } from 'react';
import { FiAlertTriangle, FiPlay, FiPause } from 'react-icons/fi';

/**
 * Confirmation for operating the pipeline. Live runs require typing LIVE.
 * kind: 'dry_run' | 'live' | 'pause' | 'resume'
 */
const COPY = {
  dry_run: {
    title: 'Run pipeline now (dry run)?',
    body: 'Exports tomorrow+2..+4 arrivals, cleans and dedupes them, and simulates sends. No SMS leaves Vonage. Results show up in Runs within a few minutes.',
    cta: 'Start dry run',
    tone: 'bg-zinc-900 hover:bg-zinc-800',
    icon: FiPlay,
  },
  live: {
    title: 'Send real SMS to guests now?',
    body: 'This triggers a LIVE run outside the normal noon schedule. Every eligible guest in the arrival window who has not already been texted will receive a message. Type LIVE to confirm.',
    cta: 'Send live',
    tone: 'bg-rose-600 hover:bg-rose-700',
    icon: FiAlertTriangle,
    confirmWord: 'LIVE',
  },
  pause: {
    title: 'Pause the daily schedule?',
    body: 'The noon run will not fire until resumed. Runs already in progress finish normally. Use this as the emergency stop.',
    cta: 'Pause schedule',
    tone: 'bg-amber-600 hover:bg-amber-700',
    icon: FiPause,
  },
  resume: {
    title: 'Resume the daily schedule?',
    body: 'Airflow may immediately create a catch-up run for the most recent missed noon slot.',
    cta: 'Resume schedule',
    tone: 'bg-emerald-600 hover:bg-emerald-700',
    icon: FiPlay,
  },
};

export default function ConfirmRunModal({ kind, busy = false, error = '', onCancel, onConfirm }) {
  const [typed, setTyped] = useState('');
  useEffect(() => setTyped(''), [kind]);

  if (!kind) return null;
  const copy = COPY[kind];
  const Icon = copy.icon;
  const needsWord = Boolean(copy.confirmWord);
  const canConfirm = !busy && (!needsWord || typed.trim() === copy.confirmWord);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
      onClick={busy ? undefined : onCancel}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="sms-confirm-title"
        className="mx-4 w-full max-w-md rounded-[28px] bg-white p-6 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${kind === 'live' ? 'bg-rose-50 text-rose-600' : 'bg-zinc-100 text-zinc-700'}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 id="sms-confirm-title" className="text-lg font-semibold text-zinc-900">
              {copy.title}
            </h3>
            <p className="mt-1.5 text-sm text-zinc-500">{copy.body}</p>
          </div>
        </div>

        {needsWord ? (
          <input
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={`Type ${copy.confirmWord}`}
            className="mt-4 h-10 w-full rounded-xl border border-zinc-200 px-3 text-sm uppercase tracking-widest outline-none focus:border-rose-300 focus:ring-2 focus:ring-rose-100"
          />
        ) : null}

        {error ? <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</p> : null}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-xl bg-zinc-100 px-4 py-2 text-sm font-medium text-zinc-600 transition-colors hover:bg-zinc-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm}
            className={`rounded-xl px-4 py-2 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${copy.tone}`}
          >
            {busy ? 'Working…' : copy.cta}
          </button>
        </div>
      </div>
    </div>
  );
}
